import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AppConfig } from '../../config/app-config.js'
import {
  LIVE_LINE_NOT_STORED,
  LIVE_LINE_UNCONFIRMED,
  LIVE_MIC_CAPTURE_CONSTRAINTS,
} from '../../core/contracts.js'
import { logger } from '../../lib/logger-client.js'
import { LiveSessionAdapter } from '../../live/LiveSessionAdapter.js'
import {
  createLiveTranscriptState,
  getLatestTranscriptEntry,
  isTranscriptEvent,
  liveTranscriptText,
  reduceLiveTranscriptEvent,
  resetLiveTranscriptState,
  type LiveTranscriptEntry,
  type LiveTranscriptState,
} from '../../live/liveTranscripts.js'
import type { LiveToolResponseInput } from '../../live/liveTools.js'
import type {
  LiveConnectArgs,
  LiveMediaFrame,
  LiveSessionAdapter as LiveSessionAdapterContract,
  LiveSessionEvent,
  LiveSessionState,
  LiveToolCall,
} from '../../live/liveTypes.js'
import { base64ToBytes, createPcmBlob, decodeAudioData } from '../../../utils/audioUtils.js'

type LiveError = {
  message: string
  code?: string
}

type LiveAudioChunk = { mimeType: string; data: string }
type AudioCleanupOptions = { sendEnd?: boolean }

const INPUT_SAMPLE_RATE = AppConfig.api.audio.inputSampleRate
const OUTPUT_SAMPLE_RATE = AppConfig.api.audio.outputSampleRate
const AUDIO_BUFFER_SIZE = AppConfig.api.audio.bufferSize
const AUDIO_WORKLET_URL = '/audio-processor.js'
const AUDIO_LEVEL_UPDATE_INTERVAL_MS = 100

export interface UseLiveSessionOptions {
  adapter?: LiveSessionAdapterContract
  audioPlaybackEnabled?: boolean
  autoDisconnectOnUnmount?: boolean
  onEvent?: (event: LiveSessionEvent) => void
  onStateChange?: (state: LiveSessionState) => void
  onTranscript?: (entry: LiveTranscriptEntry, event: LiveSessionEvent) => void
  onToolCall?: (calls: LiveToolCall[]) => LiveToolResponseInput | Promise<LiveToolResponseInput>
  onToolStatus?: (status: 'executing' | 'complete', toolName: string) => void
  onVolumeChange?: (inputLevel: number, outputLevel: number) => void
  onError?: (error: LiveError) => void
}

export interface UseLiveSessionResult {
  adapter: LiveSessionAdapterContract
  state: LiveSessionState
  isConnected: boolean
  isConnecting: boolean
  transcriptState: LiveTranscriptState
  transcriptEntries: LiveTranscriptEntry[]
  transcriptText: string
  lastEvent: LiveSessionEvent | null
  lastError: LiveError | null
  sessionResumptionHandle: string | null
  connect: (args: LiveConnectArgs) => Promise<void>
  resumeSession: (handle: string) => Promise<void>
  disconnect: () => Promise<void>
  sendText: (text: string) => void
  sendAudioChunk: (chunk: LiveAudioChunk) => void
  sendAudioStreamEnd: () => void
  startAudioInput: (stream?: MediaStream) => Promise<void>
  stopAudioInput: () => Promise<void>
  setAudioInputMuted: (muted: boolean) => void
  setAudioOutputMuted: (muted: boolean) => void
  sendVideoFrame: (frame: LiveMediaFrame) => boolean
  sendToolResponse: (response: unknown) => void
  resetTranscripts: () => void
  getSessionResumptionHandle: () => string | null
  isAudioInputActive: boolean
  isAudioInputMuted: boolean
  isAudioOutputMuted: boolean
  inputLevel: number
  outputLevel: number
  getAudioData: () => { waveform?: Uint8Array; frequency?: Uint8Array } | null
}

type CallbackRefs = Pick<
  UseLiveSessionOptions,
  | 'audioPlaybackEnabled'
  | 'autoDisconnectOnUnmount'
  | 'onError'
  | 'onEvent'
  | 'onStateChange'
  | 'onToolCall'
  | 'onToolStatus'
  | 'onTranscript'
  | 'onVolumeChange'
>

export function useLiveSession(options: UseLiveSessionOptions = {}): UseLiveSessionResult {
  const adapterRef = useRef<LiveSessionAdapterContract | null>(null)
  const callbacksRef = useRef<CallbackRefs>({})
  const transcriptStateRef = useRef<LiveTranscriptState>(createLiveTranscriptState())
  const stateRef = useRef<LiveSessionState>('disconnected')
  const inputAudioContextRef = useRef<AudioContext | null>(null)
  const outputAudioContextRef = useRef<AudioContext | null>(null)
  const inputStreamRef = useRef<MediaStream | null>(null)
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const inputAnalyserRef = useRef<AnalyserNode | null>(null)
  const inputSilenceGainRef = useRef<GainNode | null>(null)
  const outputAnalyserRef = useRef<AnalyserNode | null>(null)
  const outputGainRef = useRef<GainNode | null>(null)
  const processorRef = useRef<AudioWorkletNode | ScriptProcessorNode | null>(null)
  const activeOutputSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())
  const nextAudioStartTimeRef = useRef(0)
  const lastAudioSendRef = useRef(0)
  const audioLevelFrameRef = useRef<number | null>(null)
  const inputLevelRef = useRef(0)
  const outputLevelRef = useRef(0)
  const audioInputMutedRef = useRef(false)
  const audioOutputMutedRef = useRef(false)
  const audioInterruptionGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cleanupAudioInputRef = useRef<((options?: AudioCleanupOptions) => Promise<void>) | null>(
    null
  )
  const isStartingAudioInputRef = useRef(false)
  const audioInputStartTokenRef = useRef(0)
  // Scratch buffers for canvas audio visualization (reused to avoid GC)
  const waveformBufferRef = useRef<Uint8Array | null>(null)
  const frequencyBufferRef = useRef<Uint8Array | null>(null)

  if (!adapterRef.current) {
    adapterRef.current = options.adapter ?? new LiveSessionAdapter()
    stateRef.current = adapterRef.current.getState()
  }

  const adapter = adapterRef.current

  const [state, setState] = useState<LiveSessionState>(() => adapter.getState())
  const [transcriptState, setTranscriptState] = useState<LiveTranscriptState>(() =>
    createLiveTranscriptState()
  )
  const [lastEvent, setLastEvent] = useState<LiveSessionEvent | null>(null)
  const [lastError, setLastError] = useState<LiveError | null>(null)
  const [sessionResumptionHandle, setSessionResumptionHandle] = useState<string | null>(() =>
    adapter.getSessionResumptionHandle()
  )
  const [isAudioInputActive, setIsAudioInputActive] = useState(false)
  const [isAudioInputMuted, setIsAudioInputMuted] = useState(false)
  const [isAudioOutputMuted, setIsAudioOutputMuted] = useState(false)
  const [inputLevel, setInputLevel] = useState(0)
  const [outputLevel, setOutputLevel] = useState(0)

  useEffect(() => {
    callbacksRef.current = {
      ...(options.audioPlaybackEnabled !== undefined
        ? { audioPlaybackEnabled: options.audioPlaybackEnabled }
        : {}),
      ...(options.autoDisconnectOnUnmount !== undefined
        ? { autoDisconnectOnUnmount: options.autoDisconnectOnUnmount }
        : {}),
      ...(options.onError ? { onError: options.onError } : {}),
      ...(options.onEvent ? { onEvent: options.onEvent } : {}),
      ...(options.onStateChange ? { onStateChange: options.onStateChange } : {}),
      ...(options.onToolCall ? { onToolCall: options.onToolCall } : {}),
      ...(options.onToolStatus ? { onToolStatus: options.onToolStatus } : {}),
      ...(options.onTranscript ? { onTranscript: options.onTranscript } : {}),
      ...(options.onVolumeChange ? { onVolumeChange: options.onVolumeChange } : {}),
    }
  }, [
    options.audioPlaybackEnabled,
    options.autoDisconnectOnUnmount,
    options.onError,
    options.onEvent,
    options.onStateChange,
    options.onToolCall,
    options.onToolStatus,
    options.onTranscript,
    options.onVolumeChange,
  ])

  const syncState = useCallback(
    (nextState: LiveSessionState = adapter.getState()) => {
      if (stateRef.current === nextState) return

      stateRef.current = nextState
      setState(nextState)
      callbacksRef.current.onStateChange?.(nextState)
    },
    [adapter]
  )

  const applyTranscriptEvent = useCallback((event: LiveSessionEvent) => {
    const nextState = reduceLiveTranscriptEvent(transcriptStateRef.current, event)
    if (nextState === transcriptStateRef.current) return

    transcriptStateRef.current = nextState
    setTranscriptState(nextState)

    if (isTranscriptEvent(event)) {
      const latest = getLatestEntryForEvent(nextState, event)
      if (latest) {
        callbacksRef.current.onTranscript?.(latest, event)
      }
    }
  }, [])

  const startAudioLevelLoop = useCallback(() => {
    if (audioLevelFrameRef.current !== null) return
    let lastLevelUpdateAt = 0

    const resetLevels = () => {
      if (inputLevelRef.current !== 0) {
        inputLevelRef.current = 0
        setInputLevel(0)
      }
      if (outputLevelRef.current !== 0) {
        outputLevelRef.current = 0
        setOutputLevel(0)
      }
      callbacksRef.current.onVolumeChange?.(0, 0)
    }

    const hasActiveAudioSource = () => {
      const audioTrack = inputStreamRef.current?.getAudioTracks()[0]
      const hasActiveInput = Boolean(
        inputAnalyserRef.current &&
          inputAudioContextRef.current?.state !== 'closed' &&
          (!audioTrack || audioTrack.readyState !== 'ended')
      )
      const hasActiveOutput = Boolean(
        outputAnalyserRef.current && activeOutputSourcesRef.current.size > 0
      )
      const sessionState = stateRef.current
      const sessionActive =
        sessionState === 'connected' ||
        sessionState === 'connecting' ||
        sessionState === 'reconnecting'

      return sessionActive && (hasActiveInput || hasActiveOutput)
    }

    const tick = (timestamp?: number) => {
      if (!hasActiveAudioSource()) {
        audioLevelFrameRef.current = null
        resetLevels()
        return
      }

      const now = timestamp ?? (typeof performance !== 'undefined' ? performance.now() : Date.now())
      if (lastLevelUpdateAt > 0 && now - lastLevelUpdateAt < AUDIO_LEVEL_UPDATE_INTERVAL_MS) {
        audioLevelFrameRef.current = requestAudioFrame(tick)
        return
      }
      lastLevelUpdateAt = now

      const nextInputLevel = readAnalyserLevel(inputAnalyserRef.current)
      const nextOutputLevel = readAnalyserLevel(outputAnalyserRef.current)

      const inputChanged = Math.abs(nextInputLevel - inputLevelRef.current) >= 0.01
      const outputChanged = Math.abs(nextOutputLevel - outputLevelRef.current) >= 0.01
      if (inputChanged) {
        inputLevelRef.current = nextInputLevel
        setInputLevel(nextInputLevel)
      }
      if (outputChanged) {
        outputLevelRef.current = nextOutputLevel
        setOutputLevel(nextOutputLevel)
      }
      if (inputChanged || outputChanged) {
        callbacksRef.current.onVolumeChange?.(nextInputLevel, nextOutputLevel)
      }

      audioLevelFrameRef.current = requestAudioFrame(tick)
    }

    audioLevelFrameRef.current = requestAudioFrame(tick)
  }, [])

  const stopAudioLevelLoop = useCallback(() => {
    if (audioLevelFrameRef.current === null) return
    cancelAudioFrame(audioLevelFrameRef.current)
    audioLevelFrameRef.current = null
  }, [])

  const clearAudioPlayback = useCallback(() => {
    for (const source of activeOutputSourcesRef.current) {
      try {
        source.stop(0)
      } catch {
        /* noop */
      }

      try {
        source.disconnect()
      } catch {
        /* noop */
      }
    }

    activeOutputSourcesRef.current.clear()
    nextAudioStartTimeRef.current = 0

    const outputGain = outputGainRef.current
    const context = outputAudioContextRef.current
    if (outputGain && context) {
      const now = context.currentTime
      outputGain.gain.cancelScheduledValues(now)
      outputGain.gain.setValueAtTime(audioOutputMutedRef.current ? 0 : 1, now)
    }
  }, [])

  const ensureOutputAudioContext = useCallback(async () => {
    if (typeof window === 'undefined') {
      throw new Error('Web Audio API is not available')
    }

    const AudioContextCtor = getAudioContextCtor()
    if (!AudioContextCtor) {
      throw new Error('Web Audio API is not available')
    }

    let context = outputAudioContextRef.current
    if (!context) {
      context = new AudioContextCtor({ sampleRate: OUTPUT_SAMPLE_RATE })
      outputAudioContextRef.current = context
    }

    if (context.state === 'suspended') {
      await context.resume()
    }

    if (!outputGainRef.current || !outputAnalyserRef.current) {
      const outputGain = context.createGain()
      const outputAnalyser = context.createAnalyser()
      outputAnalyser.fftSize = 256
      outputAnalyser.smoothingTimeConstant = 0.3
      outputGain.gain.value = audioOutputMutedRef.current ? 0 : 1
      outputGain.connect(outputAnalyser)
      outputAnalyser.connect(context.destination)
      outputGainRef.current = outputGain
      outputAnalyserRef.current = outputAnalyser
    }

    startAudioLevelLoop()
    return context
  }, [startAudioLevelLoop])

  const playAudioOutput = useCallback(
    async (pcmBase64: string, mimeType?: string) => {
      if (callbacksRef.current.audioPlaybackEnabled === false || audioOutputMutedRef.current) {
        return
      }
      if (adapter.getState() !== 'connected') {
        return
      }

      try {
        const context = await ensureOutputAudioContext()
        const outputGain = outputGainRef.current
        if (!outputGain) return

        const bytes = base64ToBytes(pcmBase64)
        const audioBuffer = decodeAudioData(
          bytes,
          context,
          readPcmSampleRate(mimeType, OUTPUT_SAMPLE_RATE)
        )
        const source = context.createBufferSource()
        source.buffer = audioBuffer
        source.connect(outputGain)
        activeOutputSourcesRef.current.add(source)
        source.onended = () => {
          activeOutputSourcesRef.current.delete(source)
        }

        const now = context.currentTime
        const startTime = Math.max(now, nextAudioStartTimeRef.current)
        source.start(startTime)
        nextAudioStartTimeRef.current = startTime + audioBuffer.duration
      } catch (error) {
        logger.error(
          '[useLiveSession] Failed to play Live audio output',
          error instanceof Error ? error : new Error(String(error))
        )
      }
    },
    [adapter, ensureOutputAudioContext]
  )

  const cleanupAudioOutput = useCallback(async () => {
    clearAudioPlayback()

    const outputGain = outputGainRef.current
    const outputAnalyser = outputAnalyserRef.current
    const context = outputAudioContextRef.current

    try {
      outputGain?.disconnect()
    } catch {
      /* noop */
    }
    try {
      outputAnalyser?.disconnect()
    } catch {
      /* noop */
    }
    try {
      await context?.close()
    } catch {
      /* noop */
    }

    outputGainRef.current = null
    outputAnalyserRef.current = null
    outputAudioContextRef.current = null
    outputLevelRef.current = 0
    setOutputLevel(0)

    if (!inputAnalyserRef.current) {
      stopAudioLevelLoop()
    }
  }, [clearAudioPlayback, stopAudioLevelLoop])

  const sendMicrophoneFrame = useCallback(
    (inputData: Float32Array) => {
      if (audioInputMutedRef.current || adapter.getState() !== 'connected') {
        return
      }

      const audioTrack = inputStreamRef.current?.getAudioTracks()[0]
      if (audioTrack && audioTrack.readyState === 'ended') {
        if (!audioInterruptionGraceTimerRef.current) {
          logger.warn(
            '[useLiveSession] Audio track ended; waiting briefly before stopping input'
          )
          audioInterruptionGraceTimerRef.current = setTimeout(() => {
            const currentTrack = inputStreamRef.current?.getAudioTracks()[0]
            if (!currentTrack || currentTrack.readyState === 'ended') {
              // File picker / OS interruption can end the mic track. Do not
              // audioStreamEnd — that used to activityEnd the Fly session when
              // the user was attaching a PDF. Local input stops; socket stays.
              void cleanupAudioInputRef.current?.({ sendEnd: false })
            }
            audioInterruptionGraceTimerRef.current = null
          }, 3000)
        }
        return
      }

      if (audioInterruptionGraceTimerRef.current) {
        clearTimeout(audioInterruptionGraceTimerRef.current)
        audioInterruptionGraceTimerRef.current = null
      }

      const pcmBlob = createPcmBlob(inputData, INPUT_SAMPLE_RATE)
      if (pcmBlob.mimeType && pcmBlob.data) {
        adapter.sendAudioChunk({ mimeType: pcmBlob.mimeType, data: pcmBlob.data })
      }
    },
    [adapter]
  )

  const cleanupAudioInput = useCallback(
    async (cleanupOptions: AudioCleanupOptions = {}) => {
      const shouldSendEnd = cleanupOptions.sendEnd !== false
      logger.debug('[cleanupAudioInput] called', { shouldSendEnd })

      // Increment token to cancel any in-flight startAudioInput
      ++audioInputStartTokenRef.current

      if (audioInterruptionGraceTimerRef.current) {
        clearTimeout(audioInterruptionGraceTimerRef.current)
        audioInterruptionGraceTimerRef.current = null
      }

      // Capture refs locally and clear only if they still match
      const processor = processorRef.current
      if (processor) {
        if ('port' in processor) {
          processor.port.onmessage = null
        }
        try {
          processor.disconnect()
        } catch {
          /* noop */
        }
        if (processorRef.current === processor) {
          processorRef.current = null
        }
      }

      const inputSource = inputSourceRef.current
      try {
        inputSource?.disconnect()
      } catch {
        /* noop */
      }
      if (inputSourceRef.current === inputSource) {
        inputSourceRef.current = null
      }

      const inputSilenceGain = inputSilenceGainRef.current
      try {
        inputSilenceGain?.disconnect()
      } catch {
        /* noop */
      }
      if (inputSilenceGainRef.current === inputSilenceGain) {
        inputSilenceGainRef.current = null
      }

      const inputAnalyser = inputAnalyserRef.current
      if (inputAnalyserRef.current === inputAnalyser) {
        inputAnalyserRef.current = null
      }

      const inputStream = inputStreamRef.current
      inputStream?.getTracks().forEach(track => {
        try {
          track.stop()
        } catch {
          /* noop */
        }
      })
      if (inputStreamRef.current === inputStream) {
        inputStreamRef.current = null
      }

      const inputContext = inputAudioContextRef.current
      try {
        await inputContext?.close()
      } catch {
        /* noop */
      }
      if (inputAudioContextRef.current === inputContext) {
        inputAudioContextRef.current = null
      }

      inputLevelRef.current = 0
      setIsAudioInputActive(false)
      setInputLevel(0)

      if (!outputAnalyserRef.current) {
        stopAudioLevelLoop()
      }

      if (shouldSendEnd && adapter.getState() === 'connected') {
        adapter.sendAudioStreamEnd()
      }
    },
    [adapter, stopAudioLevelLoop]
  )

  useEffect(() => {
    cleanupAudioInputRef.current = cleanupAudioInput
  }, [cleanupAudioInput])

  const startAudioInput = useCallback(
    async (stream?: MediaStream) => {
      logger.debug('[startAudioInput] START', { hasStream: !!stream, adapterState: adapter.getState() })
      // GUARD 1: Session must be connected. A mic-stop error can mark the
      // adapter failed while the Fly socket is still open — resume that
      // socket instead of demanding a second session_ready.
      if (adapter.getState() !== 'connected') {
        const resumed = adapter.resumeOpenTransport?.() === true
        if (resumed) {
          syncState('connected')
        } else {
          logger.warn('[startAudioInput] GUARD 1: adapter not connected', { state: adapter.getState() })
          const error = new Error('Live session must be connected before starting audio input')
          const liveError = toLiveError(error, 'Failed to start Live audio input')
          setLastError(liveError)
          callbacksRef.current.onError?.(liveError)
          throw error
        }
      }

      // A Stop that lost the race can leave a closed graph while the button
      // is already back on Start. Drop it and arm the mic again.
      const leftoverGraph =
        !isAudioInputActive &&
        (inputAudioContextRef.current != null ||
          inputStreamRef.current != null ||
          processorRef.current != null)
      if (leftoverGraph) {
        await cleanupAudioInput({ sendEnd: false })
      }

      const liveGraph =
        isAudioInputActive &&
        inputAudioContextRef.current != null &&
        inputAudioContextRef.current.state !== 'closed'
      if (isStartingAudioInputRef.current || liveGraph) {
        logger.debug('[startAudioInput] GUARD 2: already starting or active')
        return
      }
      if (inputAudioContextRef.current?.state === 'closed') {
        inputAudioContextRef.current = null
      }
      isStartingAudioInputRef.current = true
      const myToken = ++audioInputStartTokenRef.current

      // Capture all node instances for safe cleanup
      let inputContext: AudioContext | null = null
      let capturedSource: MediaStreamAudioSourceNode | null = null
      let capturedAnalyser: AnalyserNode | null = null
      let capturedSilenceGain: GainNode | null = null
      let capturedStream: MediaStream | null = null
      let capturedProcessor: AudioWorkletNode | ScriptProcessorNode | null = null

      const isStillCurrent = () => audioInputStartTokenRef.current === myToken
      const abandonStartup = async () => {
        if (inputContext && inputAudioContextRef.current === inputContext) {
          inputAudioContextRef.current = null
        }
        await inputContext?.close().catch(() => {})
        if (capturedStream && inputStreamRef.current === capturedStream) {
          inputStreamRef.current = null
          capturedStream.getTracks().forEach(track => track.stop())
        }
      }

      try {
        const AudioContextCtor = getAudioContextCtor()
        if (!AudioContextCtor) {
          throw new Error('Web Audio API is not available')
        }

        logger.debug('[startAudioInput] AudioContext creating', { sampleRate: INPUT_SAMPLE_RATE })
        inputContext = new AudioContextCtor({ sampleRate: INPUT_SAMPLE_RATE })
        inputAudioContextRef.current = inputContext
        logger.debug('[startAudioInput] AudioContext created', { state: inputContext.state })

        // GUARD 3: Cancellation token — if cleanup ran while we were awaiting, abort
        if (!isStillCurrent()) {
          await abandonStartup()
          logger.debug('[startAudioInput] token changed after AudioContext create — abort')
          return
        }

        if (inputContext.state === 'suspended') {
          logger.debug('[startAudioInput] AudioContext suspended — resuming...')
          await inputContext.resume()
          logger.debug('[startAudioInput] AudioContext resumed', { state: inputContext.state })
        }

        // GUARD 4: Check token again after async resume
        if (!isStillCurrent()) {
          await abandonStartup()
          logger.debug('[startAudioInput] token changed after resume — abort')
          return
        }

        await ensureOutputAudioContext()
        logger.debug('[startAudioInput] output AudioContext ensured')

        // GUARD 5: Check token after output setup
        if (!isStillCurrent()) {
          await abandonStartup()
          logger.debug('[startAudioInput] token changed after output setup — abort')
          return
        }

        logger.debug('[startAudioInput] getUserMedia requesting', {
          audio: LIVE_MIC_CAPTURE_CONSTRAINTS,
        })
        const inputStream =
          stream ??
          (await getMicrophoneStream({
            audio: { ...LIVE_MIC_CAPTURE_CONSTRAINTS },
          }))
        capturedStream = inputStream
        inputStreamRef.current = inputStream
        logger.debug('[startAudioInput] getUserMedia SUCCESS', { tracks: inputStream.getTracks().map(t => t.kind) })

        // GUARD 6: Check token after mic stream acquired
        if (!isStillCurrent()) {
          await abandonStartup()
          logger.debug('[startAudioInput] token changed after getUserMedia — abort')
          return
        }

        // GUARD 7: AudioContext closed = fatal; suspended is ok (will resume)
        if (inputContext.state === 'closed') {
          logger.warn('[startAudioInput] AudioContext closed — retrying with fresh context')
          // Retry once with fresh context
          const retryContext = new AudioContextCtor({ sampleRate: INPUT_SAMPLE_RATE })
          inputContext = retryContext
          inputAudioContextRef.current = retryContext
          if (retryContext.state === 'suspended') {
            await retryContext.resume()
          }
          if (retryContext.state === 'closed' || !isStillCurrent()) {
            await retryContext.close().catch(() => {})
            throw new Error('AudioContext closed and retry failed')
          }
        }

        const inputSource = inputContext.createMediaStreamSource(inputStream)
        const inputAnalyser = inputContext.createAnalyser()
        const inputSilenceGain = inputContext.createGain()
        inputAnalyser.fftSize = 256
        inputAnalyser.smoothingTimeConstant = 0.3
        inputAnalyser.minDecibels = -90
        inputAnalyser.maxDecibels = -10
        inputSilenceGain.gain.value = 0

        capturedSource = inputSource
        capturedAnalyser = inputAnalyser
        capturedSilenceGain = inputSilenceGain
        inputSourceRef.current = inputSource
        inputAnalyserRef.current = inputAnalyser
        inputSilenceGainRef.current = inputSilenceGain

        let useWorklet = typeof AudioWorkletNode !== 'undefined' && Boolean(inputContext.audioWorklet)
        if (useWorklet) {
          try {
            logger.debug('[startAudioInput] AudioWorklet addModule', { url: AUDIO_WORKLET_URL })
            await inputContext.audioWorklet.addModule(AUDIO_WORKLET_URL)
            logger.debug('[startAudioInput] AudioWorklet addModule SUCCESS')
          } catch (error) {
            if (typeof window !== 'undefined') {
              logger.error(
                '[startAudioInput] AudioWorklet addModule FAILED; using ScriptProcessor fallback',
                error instanceof Error ? error : new Error(String(error))
              )
            }
            useWorklet = false
          }
        } else {
          logger.debug('[startAudioInput] AudioWorkletNode unavailable — using ScriptProcessor fallback')
        }

        // GUARD 8: Final token check + context state check before node creation
        // suspended is ok; closed is not
        if (!isStillCurrent() || inputContext.state === 'closed') {
          throw new Error('Audio input startup was cancelled or AudioContext closed')
        }

        if (useWorklet) {
          const processor = new AudioWorkletNode(inputContext, 'audio-recorder-worklet')
          capturedProcessor = processor
          processorRef.current = processor
          inputSource.connect(inputAnalyser)
          inputAnalyser.connect(processor)
          processor.connect(inputSilenceGain)
          inputSilenceGain.connect(inputContext.destination)
          processor.port.onmessage = (event: MessageEvent<{ audioData?: Float32Array }>) => {
            const inputData = event.data.audioData
            if (inputData) sendMicrophoneFrame(inputData)
          }
        } else {
          const processor = inputContext.createScriptProcessor(AUDIO_BUFFER_SIZE, 1, 1)
          capturedProcessor = processor
          processorRef.current = processor
          inputSource.connect(inputAnalyser)
          inputAnalyser.connect(processor)
          processor.connect(inputSilenceGain)
          inputSilenceGain.connect(inputContext.destination)
          processor.onaudioprocess = event => {
            const now = Date.now()
            if (now - lastAudioSendRef.current < 100) return
            lastAudioSendRef.current = now
            sendMicrophoneFrame(event.inputBuffer.getChannelData(0))
          }
        }

        setIsAudioInputActive(true)
        startAudioLevelLoop()
      } catch (error) {
        // Safe cleanup: only clear refs that still point to our captured instances
        if (inputAudioContextRef.current === inputContext) {
          inputAudioContextRef.current = null
          if (inputContext) {
            await inputContext.close().catch(() => {})
          }
        }
        if (inputSourceRef.current === capturedSource) inputSourceRef.current = null
        if (inputAnalyserRef.current === capturedAnalyser) inputAnalyserRef.current = null
        if (inputSilenceGainRef.current === capturedSilenceGain) inputSilenceGainRef.current = null
        if (processorRef.current === capturedProcessor) processorRef.current = null
        if (inputStreamRef.current === capturedStream) {
          inputStreamRef.current = null
          capturedStream?.getTracks().forEach(track => track.stop())
        }

        const liveError = toLiveError(error, 'Failed to start Live audio input')
        setLastError(liveError)
        callbacksRef.current.onError?.(liveError)
        if (typeof window !== 'undefined') {
          logger.error(
            '[useLiveSession] Failed to start Live audio input',
            error instanceof Error ? error : new Error(String(error))
          )
        }
        throw error
      } finally {
        isStartingAudioInputRef.current = false
      }
    },
    [
      cleanupAudioInput,
      ensureOutputAudioContext,
      isAudioInputActive,
      sendMicrophoneFrame,
      startAudioLevelLoop,
      syncState,
    ]
  )

  const stopAudioInput = useCallback(async () => {
    await cleanupAudioInput({ sendEnd: true })
  }, [cleanupAudioInput])

  const setAudioInputMuted = useCallback(
    (muted: boolean) => {
      audioInputMutedRef.current = muted
      setIsAudioInputMuted(muted)
      if (muted && adapter.getState() === 'connected') {
        adapter.sendAudioStreamEnd()
      }
    },
    [adapter]
  )

  const setAudioOutputMuted = useCallback((muted: boolean) => {
    audioOutputMutedRef.current = muted
    setIsAudioOutputMuted(muted)
    const outputGain = outputGainRef.current
    const context = outputAudioContextRef.current
    if (outputGain && context) {
      outputGain.gain.setValueAtTime(muted ? 0 : 1, context.currentTime)
    }
  }, [])

  const handleAdapterEvent = useCallback(
    (event: LiveSessionEvent) => {
      setLastEvent(event)
      applyTranscriptEvent(event)

      switch (event.type) {
        case 'connected':
        case 'resumed':
          syncState(adapter.getState())
          break

        case 'disconnected':
          syncState(adapter.getState())
          void cleanupAudioInput({ sendEnd: false })
          void cleanupAudioOutput()
          break

        case 'error':
          if (event.code === LIVE_LINE_NOT_STORED || event.code === LIVE_LINE_UNCONFIRMED) break
          syncState(adapter.getState())
          void cleanupAudioInput({ sendEnd: false })
          void cleanupAudioOutput()
          break

        case 'sessionResumptionUpdate':
          setSessionResumptionHandle(event.handle)
          break

        case 'toolCall': {
          const maybeResponse = callbacksRef.current.onToolCall?.(event.calls)
          if (maybeResponse !== undefined) {
            void Promise.resolve(maybeResponse).then(response => {
              if (response !== undefined && response !== null) {
                adapter.sendToolResponse(response)
              }
            }).catch(error => {
              logger.error('[useLiveSession] Tool call handler failed', error)
            })
          }
          break
        }

        case 'audioOutput':
          void playAudioOutput(event.pcmBase64, event.mimeType)
          break

        case 'interrupted':
          clearAudioPlayback()
          break

        case 'toolStatus':
          // Dead air bridge: tool executing → UI shows indicator, tool complete → hide
          callbacksRef.current.onToolStatus?.(event.status, event.toolName)
          break

        default:
          break
      }

      if (event.type === 'error') {
        const error = event.code
          ? { message: event.message, code: event.code }
          : { message: event.message }
        setLastError(error)
        callbacksRef.current.onError?.(error)
      }

      callbacksRef.current.onEvent?.(event)
    },
    [
      adapter,
      applyTranscriptEvent,
      cleanupAudioInput,
      cleanupAudioOutput,
      clearAudioPlayback,
      playAudioOutput,
      syncState,
    ]
  )

  useEffect(() => {
    const unsubscribe = adapter.onEvent(handleAdapterEvent)
    return () => {
      unsubscribe()
      void cleanupAudioInput({ sendEnd: false })
      void cleanupAudioOutput()
      const shouldDisconnect = callbacksRef.current.autoDisconnectOnUnmount !== false
      if (shouldDisconnect) {
        void adapter.disconnect().catch(error => {
          logger.error('[useLiveSession] Failed to disconnect on unmount', error)
        })
      }
    }
  }, [adapter, cleanupAudioInput, cleanupAudioOutput, handleAdapterEvent])

  const connect = useCallback(
    async (args: LiveConnectArgs) => {
      logger.debug('[useLiveSession.connect] START', { args })
      syncState('connecting')
      setLastError(null)

      try {
        await adapter.connect(args)
        syncState(adapter.getState())
        setSessionResumptionHandle(adapter.getSessionResumptionHandle())
        logger.debug('[useLiveSession.connect] SUCCESS', { state: adapter.getState(), sessionResumptionHandle: adapter.getSessionResumptionHandle() })
      } catch (error) {
        syncState(adapter.getState())
        const liveError = toLiveError(error, 'Failed to connect Live session')
        setLastError(liveError)
        callbacksRef.current.onError?.(liveError)
        logger.debug('[useLiveSession.connect] FAILED', { error: liveError })
        throw error
      }
    },
    [adapter, syncState]
  )

  const resumeSession = useCallback(
    async (handle: string) => {
      syncState('reconnecting')
      setLastError(null)

      try {
        await adapter.resumeSession(handle)
        syncState(adapter.getState())
        setSessionResumptionHandle(adapter.getSessionResumptionHandle())
      } catch (error) {
        syncState(adapter.getState())
        const liveError = toLiveError(error, 'Failed to resume Live session')
        setLastError(liveError)
        callbacksRef.current.onError?.(liveError)
        throw error
      }
    },
    [adapter, syncState]
  )

  const disconnect = useCallback(async () => {
    try {
      await cleanupAudioInput({ sendEnd: true })
      await cleanupAudioOutput()
      await adapter.disconnect()
    } finally {
      syncState(adapter.getState())
      setSessionResumptionHandle(adapter.getSessionResumptionHandle())
    }
  }, [adapter, cleanupAudioInput, cleanupAudioOutput, syncState])

  const sendText = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return

      const currentState = adapter.getState()
      adapter.sendRealtimeText(trimmed)
      if (currentState === 'connected') {
        applyTranscriptEvent({
          type: 'inputTranscript',
          text: trimmed,
          isFinal: true,
        })
      }
    },
    [adapter, applyTranscriptEvent]
  )

  const sendAudioChunk = useCallback(
    (chunk: LiveAudioChunk) => {
      adapter.sendAudioChunk(chunk)
    },
    [adapter]
  )

  const sendAudioStreamEnd = useCallback(() => {
    adapter.sendAudioStreamEnd()
  }, [adapter])

  const sendVideoFrame = useCallback(
    (frame: LiveMediaFrame): boolean => {
      return adapter.sendVideoFrame(frame)
    },
    [adapter]
  )

  const sendToolResponse = useCallback(
    (response: unknown) => {
      adapter.sendToolResponse(response)
    },
    [adapter]
  )

  const resetTranscripts = useCallback(() => {
    const emptyState = resetLiveTranscriptState()
    transcriptStateRef.current = emptyState
    setTranscriptState(emptyState)
  }, [])

  const getSessionResumptionHandle = useCallback(() => {
    return adapter.getSessionResumptionHandle()
  }, [adapter])

  const getAudioData = useCallback(() => {
    const inputAnalyser = inputAnalyserRef.current
    const outputAnalyser = outputAnalyserRef.current
    const activeAnalyser = pickVoiceAnalyser(inputAnalyser, outputAnalyser)
    if (!activeAnalyser) return null

    const fftSize = activeAnalyser.frequencyBinCount
    if (!waveformBufferRef.current || waveformBufferRef.current.length !== fftSize) {
      waveformBufferRef.current = new Uint8Array(fftSize)
    }
    if (!frequencyBufferRef.current || frequencyBufferRef.current.length !== fftSize) {
      frequencyBufferRef.current = new Uint8Array(fftSize)
    }

    activeAnalyser.getByteTimeDomainData(waveformBufferRef.current as any)
    activeAnalyser.getByteFrequencyData(frequencyBufferRef.current as any)

    return {
      waveform: waveformBufferRef.current,
      frequency: frequencyBufferRef.current,
    }
  }, [])

  const transcriptText = useMemo(() => liveTranscriptText(transcriptState), [transcriptState])

  return useMemo(
    () => ({
      adapter,
      state,
      isConnected: state === 'connected',
      isConnecting: state === 'connecting' || state === 'reconnecting',
      transcriptState,
      transcriptEntries: transcriptState.entries,
      transcriptText,
      lastEvent,
      lastError,
      sessionResumptionHandle,
      connect,
      resumeSession,
      disconnect,
      sendText,
      sendAudioChunk,
      sendAudioStreamEnd,
      startAudioInput,
      stopAudioInput,
      setAudioInputMuted,
      setAudioOutputMuted,
      sendVideoFrame,
      sendToolResponse,
      resetTranscripts,
      getSessionResumptionHandle,
      isAudioInputActive,
      isAudioInputMuted,
      isAudioOutputMuted,
      inputLevel,
      outputLevel,
      getAudioData,
    }),
    [
      adapter,
      state,
      transcriptState,
      transcriptText,
      lastEvent,
      lastError,
      sessionResumptionHandle,
      connect,
      resumeSession,
      disconnect,
      sendText,
      sendAudioChunk,
      sendAudioStreamEnd,
      startAudioInput,
      stopAudioInput,
      setAudioInputMuted,
      setAudioOutputMuted,
      sendVideoFrame,
      sendToolResponse,
      resetTranscripts,
      getSessionResumptionHandle,
      isAudioInputActive,
      isAudioInputMuted,
      isAudioOutputMuted,
      getAudioData,
    ]
  )
}

function getLatestEntryForEvent(
  state: LiveTranscriptState,
  event: LiveSessionEvent
): LiveTranscriptEntry | null {
  if (event.type === 'inputTranscript') {
    return getLatestTranscriptEntry(state, 'user')
  }

  if (
    event.type === 'outputTranscript' ||
    event.type === 'text' ||
    event.type === 'turnComplete' ||
    event.type === 'generationComplete'
  ) {
    return getLatestTranscriptEntry(state, 'model')
  }

  return null
}

function toLiveError(error: unknown, fallbackMessage: string): LiveError {
  if (error instanceof Error) {
    return { message: error.message || fallbackMessage }
  }

  return { message: fallbackMessage }
}

function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  return (
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  )
}

function getMicrophoneStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new Error('Microphone capture is not available'))
  }

  return navigator.mediaDevices.getUserMedia(constraints)
}

function requestAudioFrame(callback: FrameRequestCallback): number {
  if (typeof requestAnimationFrame === 'function') {
    return requestAnimationFrame(callback)
  }

  return setTimeout(() => callback(Date.now()), 16) as unknown as number
}

function cancelAudioFrame(frameId: number): void {
  if (typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(frameId)
    return
  }

  clearTimeout(frameId as unknown as ReturnType<typeof setTimeout>)
}

function runningAnalyser(analyser: AnalyserNode | null): AnalyserNode | null {
  if (!analyser || analyser.context.state !== 'running') return null
  return analyser
}

function pickVoiceAnalyser(
  inputAnalyser: AnalyserNode | null,
  outputAnalyser: AnalyserNode | null,
): AnalyserNode | null {
  const input = runningAnalyser(inputAnalyser)
  const output = runningAnalyser(outputAnalyser)
  if (!input) return output
  if (!output) return input
  return readAnalyserLevel(output) >= readAnalyserLevel(input) ? output : input
}

function readAnalyserLevel(analyser: AnalyserNode | null): number {
  if (!analyser) return 0

  const data = new Uint8Array(analyser.frequencyBinCount)
  analyser.getByteFrequencyData(data)
  if (data.length === 0) return 0

  const sumSquares = data.reduce((sum, value) => sum + (value / 255) ** 2, 0)
  return Math.sqrt(sumSquares / data.length)
}

function readPcmSampleRate(mimeType: string | undefined, fallback: number): number {
  const match = mimeType?.match(/rate=(\d+)/i)
  const parsed = match?.[1] ? Number(match[1]) : NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}
